import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import RobotImage from "../../assets/images/df104cb6e650055c4a0273f403466bfd20a63fd8.png";
import { navigateToPeerMate } from "../../utils/peermateHandoff";

function Bubble({ size, top, left, right, bottom, delay, blur = "blur-sm" }) {
  return (
    <motion.div
      animate={{
        y: [0, -20, 0],
        x: [0, 8, 0],
        scale: [1, 1.05, 1],
        rotate: [0, 40, 0],
      }}
      transition={{
        duration: 6 + Math.random() * 2,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
      style={{ top, left, right, bottom }}
      className={`absolute ${size} rounded-full bg-white/5 border border-white/10 ${blur} backdrop-blur-[1.5px] z-0`}
    />
  );
}

function PeerMateSection() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 120 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], ["6deg", "-6deg"]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], ["-6deg", "6deg"]), springConfig);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <section className="relative w-full min-h-[65vh] overflow-hidden font-sans px-6 md:px-10 lg:px-16 py-12 bg-[#1976D2] perspective-[2000px]">
      
      {/* Background Shapes & Bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -bottom-[20%] -left-[5%] w-[400px] h-[400px] bg-white/10 rounded-full blur-[70px]" />
        <div className="absolute -top-[10%] -right-[5%] w-[300px] h-[300px] bg-white/5 rounded-full blur-[60px]" />

        <Bubble size="w-14 h-14" top="10%" left="10%" delay={0} blur="blur-sm" />
        <Bubble size="w-20 h-20" top="35%" left="5%" delay={1.5} blur="blur-md" />
        <Bubble size="w-16 h-16" bottom="10%" right="10%" delay={1} blur="blur-sm" />
        <Bubble size="w-10 h-10" top="20%" right="25%" delay={2} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        
        {/* HEADER */}
        <motion.header
          className="text-center lg:text-left mb-8"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            className="h-[2px] w-20 bg-white mb-3 mx-auto lg:mx-0 rounded-full"
          />

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight mb-1">
            Meet PeerMate
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-white/70 font-medium italic">
            Your Personal AI Study Partner
          </p>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">

          {/* ROBOT */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="relative group"
          >
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-[50%] h-12 bg-gradient-to-t from-white/20 to-transparent rounded-full blur-lg" />

            <motion.img
              animate={{ y: [0, -10, 0], rotate: [-1, 1, -1] }}
              transition={{
                y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: 5, repeat: Infinity, ease: "easeInOut" }
              }}
              src={RobotImage}
              alt="PeerMate Robot"
              className="relative z-10 w-full max-w-[320px] mx-auto drop-shadow-[0_15px_30px_rgba(0,0,0,0.25)] brightness-105"
            />

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100%] h-[100%] bg-white/5 rounded-full blur-[50px] animate-pulse pointer-events-none" />
          </motion.div>

          {/* GLASS CARD */}
          <motion.div
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          >
            <div className="backdrop-blur-[20px] bg-white/10 border border-white/25 rounded-[35px] p-6 md:p-8 shadow-[0_25px_50px_-10px_rgba(0,0,0,0.25)] relative overflow-hidden">
              
              <div style={{ transform: "translateZ(40px)" }}>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-black mb-4 text-white">
                  Hi, I'm PeerMate
                </h2>

                <div className="space-y-4 text-white/90 text-sm md:text-base leading-relaxed font-medium">
                  <p>
                    I'm your personal <span className="text-white font-bold underline decoration-white/40 decoration-2 underline-offset-4">AI learning partner</span>. 
                    I transform complex subjects into structured lessons.
                  </p>

                  <p className="italic text-white border-l-2 border-white/30 pl-4 py-1">
                    "Ask me anything, practice exam-focused questions, and learn at your own pace."
                  </p>

                 {/* Added Text Section */}
<div className="pt-2 space-y-3 border-t border-white/10">
  <p className="font-semibold text-white">Why I Was Built</p>

  <p className="text-xs md:text-sm leading-snug">
    PeerMate was created to support <span className="text-white font-bold">flexible learning</span> and make education more accessible for <span className="text-white font-bold">all learners</span>, including those with disabilities.
  </p>

  <p className="text-xs md:text-sm leading-snug">
    For learners who are blind, <span className="text-white font-bold">audio guidance</span> provides clarity.  
    For learners who are deaf, <span className="text-white font-bold">text-based learning</span> ensures understanding.
  </p>

  <p className="text-xs md:text-sm italic opacity-80">
    Designed for those who prefer to learn at their own pace—without pressure, without limits.
  </p>
</div>
                </div>

                <motion.div className="mt-8" style={{ transform: "translateZ(30px)" }}>
                  <button 
                    type="button"
                    onClick={() => navigateToPeerMate()}
                    className="px-8 py-3 rounded-xl bg-white text-[#1976D2] font-bold text-sm md:text-base shadow-lg hover:bg-slate-100 transition-colors w-full sm:w-auto"
                  >
                    Start Learning
                  </button>
                </motion.div>
              </div>

              <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/5 rounded-full blur-xl" />
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/5 rounded-full blur-xl" />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export default PeerMateSection;